import { useAppSelector } from "@/core/root-reducer"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import populationStore from "@/entities/population/model/population.store"
import { Population } from "@/entities/population/model/population.types"
import PhysicalRiskUnitsSelector, {
  PhysicalRiskUnitContext
} from "@/entities/physical-risk/ui/PhysicalRiskUnitsSelector"
import siteStore from "@/entities/site/model/site.store"
import { Site } from "@/entities/site/model/site.types"
import PhysicalRiskPerimeterChoiceStep from "./PhysicalRiskPerimeterChoiceStep"

interface Props {
  linkedPopulations: Population[]
  linkedSites: Site[]
  onChange: (sites: Site[], populations: Population[]) => void
  onContinue: () => void
  onUnitTypeChange: (unitType: "site" | "population" | null) => void
  unitType: "site" | "population" | null //Pilote par le wizard, qui affiche le retour dans son footer
}

export default function PhysicalRiskWorkUnitStep({
  linkedPopulations,
  linkedSites,
  onChange,
  onContinue,
  onUnitTypeChange,
  unitType
}: Props) {
  const activeEstablishment = useAppSelector(establishmentStore.selectors.active)
  const populations = useAppSelector(populationStore.selectors.listForActiveEstablishment)
  const sites = useAppSelector(siteStore.selectors.listForActiveEstablishment)
  const selectedUnits: PhysicalRiskUnitContext[] = [
    ...linkedSites.map((site) => ({ type: "site" as const, site })),
    ...linkedPopulations.map((population) => ({ type: "population" as const, population }))
  ]
  const selectedUnit = selectedUnits[0] ?? ({ type: "establishment" } as const)

  function selectWholeEstablishment() {
    onChange([], [])
    onContinue()
  }

  function selectUnit(unit: PhysicalRiskUnitContext) {
    if (unit.type === "establishment") {
      selectWholeEstablishment()
      return
    }

    if (unit.type === "site") onChange([unit.site], [])
    else onChange([], [unit.population])

    onContinue()
  }

  function addUnit(unit: PhysicalRiskUnitContext) {
    if (unit.type === "establishment") {
      onChange([], [])
      return
    }

    if (unit.type === "site") {
      const nextSites = linkedSites.some((site) => site.id === unit.site.id)
        ? linkedSites
        : linkedSites.concat(unit.site)
      onChange(nextSites, [])
      return
    }

    const nextPopulations = linkedPopulations.some((population) => population.id === unit.population.id)
      ? linkedPopulations
      : linkedPopulations.concat(unit.population)
    onChange([], nextPopulations)
  }

  function removeUnit(unit: PhysicalRiskUnitContext) {
    if (unit.type === "establishment") {
      onChange([], [])
      return
    }

    if (unit.type === "site") {
      onChange(
        linkedSites.filter((site) => site.id !== unit.site.id),
        linkedPopulations
      )
      return
    }

    onChange(
      linkedSites,
      linkedPopulations.filter((population) => population.id !== unit.population.id)
    )
  }

  if (!unitType) {
    return (
      <PhysicalRiskPerimeterChoiceStep
        establishmentName={activeEstablishment?.name ?? ""}
        populations={populations}
        sites={sites}
        onSelectPopulations={() => onUnitTypeChange("population")}
        onSelectSites={() => onUnitTypeChange("site")}
        onSelectWholeEstablishment={selectWholeEstablishment}
      />
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PhysicalRiskUnitsSelector
        selectedUnits={selectedUnits}
        selectedUnit={selectedUnit}
        unitType={unitType}
        onAddUnit={addUnit}
        onRemoveUnit={removeUnit}
        onSelect={selectUnit}
      />
    </div>
  )
}
